import React, { useState } from "react";
import { Button, ButtonGroup, Col, Form, Row } from "react-bootstrap";
import {
    Controller,
    FormProvider,
    SubmitHandler,
    useForm,
} from "react-hook-form";
import { useLoaderData, useLocation } from "react-router";
import Select from "react-select";

import { yupResolver } from "@hookform/resolvers/yup";
import classNames from "classnames";
import * as yup from "yup";

import DeleteOrganizationModal from "./DeleteOrganizationModal";

import { store } from "../../../..";
import { alertActions } from "../../../../features/alert/alertSlice";
import {
    globalActions,
    selectSetting,
} from "../../../../features/global/globalSlice";
import { selectUsers, userActions } from "../../../../features/user/userSlice";
import { useAppDispatch, useAppSelector } from "../../../../hooks/reduxHooks";
import {
    createOrganization,
    getOrganizations,
    getTeamsBySite,
    getUser,
    updateOrganization,
} from "../../../../lib/api";
import {
    defaultDateFormat,
    displayFormAlert,
    getUserFromLocalStorage,
} from "../../../../lib/utils";
import { IOrganization, ISiteSetting, ITeam } from "../../../../types/api";
import { ILeagueMgmtOrgInfoData } from "../../../../types/forms";

export const loader = async ({ params }: { params: any }) => {
    let { orgId } = params;
    const { auth } = store.getState();

    if (!orgId) {
        const { user: userInfo } = await getUser(auth.user!.id);
        orgId = userInfo.organization;
    }

    const getOrgPromise = orgId ? getOrganizations(orgId) : Promise.resolve();
    const users = store.getState().user.users;
    const settings = store.getState().global.settings;

    const usersPromise = users.length
        ? Promise.resolve()
        : store.dispatch(userActions.getUsers());

    const settingsPromise = settings.length
        ? Promise.resolve()
        : store.dispatch(globalActions.getSettings());

    const [organization, { teams }] = await Promise.all([
        getOrgPromise,
        getTeamsBySite(),
        usersPromise,
        settingsPromise,
    ]);

    return {
        orgId,
        header: `${
            organization
                ? `Edit ${organization.organization?.name}`
                : "Add Organization"
        }`,
        teams,
        organization: organization ? organization.organization : undefined,
    };
};

const Organization = (): React.JSX.Element => {
    const dispatch = useAppDispatch();

    const location = useLocation();
    const isAddingOrg = /organizations\/add\/?$/.test(location.pathname);
    const [show, setShow] = useState<boolean>(false);

    const users = useAppSelector(selectUsers);
    const user = getUserFromLocalStorage();

    const { setting: currentYear } = useAppSelector(state =>
        selectSetting(state, "majorybl_current_league_year"),
    ) as { setting: ISiteSetting["setting"] };

    const { organization: loadedOrganization, teams } = useLoaderData() as {
        organization: IOrganization | undefined;
        teams: ITeam[];
    };

    const [organization, setOrganization] = useState<IOrganization | undefined>(
        loadedOrganization,
    );

    const orgFormSchema = yup.object().shape({
        name: yup.string().required("Please specify the organization name"),
        primaryUser: yup.object().shape({
            value: yup.number().required(),
            label: yup.string().required("Please select the primary org. user"),
        }),
        majorTeams: yup
            .array(
                yup.object().shape({
                    value: yup.number(),
                    label: yup.string(),
                }),
            )
            .optional(),
        WITeams: yup
            .array(
                yup.object().shape({
                    value: yup.number(),
                    label: yup.string(),
                }),
            )
            .optional(),
        ILTeams: yup
            .array(
                yup.object().shape({
                    value: yup.number(),
                    label: yup.string(),
                }),
            )
            .optional(),
        approved: yup.boolean().optional(),
    });

    const methods = useForm<ILeagueMgmtOrgInfoData>({
        mode: "onChange",
        resolver: yupResolver(orgFormSchema),
    });

    const onSubmit: SubmitHandler<ILeagueMgmtOrgInfoData> = async data => {
        const orgPromise = isAddingOrg
            ? createOrganization(data, user!.id)
            : updateOrganization(organization!.id, data, user!.id);

        await orgPromise
            .then(response => {
                const alertAction = isAddingOrg ? "created" : "updated";

                dispatch(
                    alertActions.success({
                        title: `Organization ${alertAction} successfully`,
                        dismissible: true,
                    }),
                );

                isAddingOrg
                    ? methods.reset()
                    : setOrganization(response.organization);
            })
            .catch(e => {
                const alertAction = isAddingOrg ? "creating" : "updating";

                dispatch(
                    alertActions.error({
                        title: `The server encountered a problem ${alertAction} the organization.`,
                        text: e,
                        dismissible: true,
                    }),
                );
            });
    };

    const defaultUser = users.find(u => u.id === organization?.primaryUser.id)
        ? {
              value: organization!.primaryUser.id,
              label: `${organization?.primaryUser.name} (${organization?.primaryUser.email})`,
          }
        : undefined;

    const watchMajorTeams = methods.watch("majorTeams");
    const watchWITeams = methods.watch("WITeams");
    const watchILTeams = methods.watch("ILTeams");

    const isOrgApproved = methods.watch(
        "approved",
        organization?.approved === 1 ? true : false,
    );

    const majorTeams = organization
        ? organization?.teams.majorTeams.map(t => {
              return {
                  value: t.id,
                  label: `${t.name} ${t.ageLevel} - (${t.coach.name} - ${t.coach.email})`,
              };
          })
        : [];

    const WITeams = organization
        ? organization?.teams.WITeams.map(t => {
              return {
                  value: t.id,
                  label: `${t.name} (${t.ageLevel}) - (${t.coach.name} - ${t.coach.email})`,
              };
          })
        : [];

    const ILTeams = organization
        ? organization?.teams.ILTeams.map(t => {
              return {
                  value: t.id,
                  label: `${t.name} (${t.ageLevel}}) - (${t.coach.name} - ${t.coach.email})`,
              };
          })
        : [];

    const teamsList = teams
        ? [...teams]
              .sort((a, b) => {
                  const nameCompare = a.name.localeCompare(b.name);

                  if (nameCompare !== 0) {
                      return nameCompare;
                  } else {
                      const ageLevelA = a.ageLevels.find(
                          a => a.year === parseInt(currentYear),
                      )?.ageLevel;

                      const ageLevelB = b.ageLevels.find(
                          a => a.year === parseInt(currentYear),
                      )?.ageLevel;

                      const ageComparison =
                          ageLevelA && ageLevelB
                              ? parseInt(ageLevelA) - parseInt(ageLevelB)
                              : ageLevelA
                                ? -1
                                : ageLevelB
                                  ? 1
                                  : 0;

                      if (ageComparison === 0) {
                          return a.name.localeCompare(b.name);
                      }

                      return ageComparison;
                  }
              })
              .map(t => {
                  const ageLevel = t.ageLevels.find(
                      a => a.year === parseInt(currentYear),
                  )?.ageLevel;

                  return {
                      value: t.id,
                      label: `${t.name}${ageLevel ? ` (${ageLevel})` : ""} - (${
                          t.coach.firstName
                      } ${t.coach.lastName} - ${t.coach.email})`,
                  };
              })
        : [];

    return (
        <section className="orgInfo">
            <FormProvider {...methods}>
                <Form
                    onSubmit={methods.handleSubmit(onSubmit)}
                    id="leagueMgmtOrgInfoForm"
                >
                    <Row>
                        <Form.Group controlId="name" as={Col} md={5}>
                            <Form.Label>Organization Name</Form.Label>
                            <Form.Control
                                type="text"
                                {...methods.register("name", {
                                    value: organization?.name,
                                })}
                                aria-invalid={
                                    methods.formState.errors.name
                                        ? "true"
                                        : "false"
                                }
                            />
                            {methods.formState.errors.name &&
                                displayFormAlert({
                                    level: "danger",
                                    text: methods.formState.errors.name
                                        .message as string,
                                })}
                        </Form.Group>
                        {user!.userLevel! === 4 &&
                            organization?.approved === 0 && (
                                <Col md={7}>
                                    <h4
                                        style={{
                                            color: "#f00",
                                            marginTop: "2em",
                                            textAlign: "center",
                                        }}
                                    >
                                        Organizational changes pending admin
                                        approval
                                    </h4>
                                </Col>
                            )}
                        {!isAddingOrg && user!.userLevel! <= 2 && (
                            <Form.Group controlId="approved" as={Col} md={3}>
                                <Form.Label>
                                    Organization Teams Approved
                                </Form.Label>
                                <Form.Control
                                    type="hidden"
                                    {...methods.register("approved", {
                                        value:
                                            methods.getValues("approved") ||
                                            organization?.approved === 1
                                                ? true
                                                : false,
                                    })}
                                />
                                <br />
                                <ButtonGroup>
                                    <Button
                                        variant={
                                            isOrgApproved
                                                ? "primary"
                                                : "outline-primary"
                                        }
                                        onClick={() =>
                                            methods.setValue("approved", true)
                                        }
                                    >
                                        Yes
                                    </Button>
                                    <Button
                                        variant={
                                            isOrgApproved
                                                ? "outline-primary"
                                                : "primary"
                                        }
                                        onClick={() =>
                                            methods.setValue("approved", false)
                                        }
                                    >
                                        No
                                    </Button>
                                </ButtonGroup>
                            </Form.Group>
                        )}
                        {!isAddingOrg && user!.userLevel! <= 2 && (
                            <Col md={2}>
                                <Form.Label>Approved By</Form.Label>
                                <br />
                                {organization?.approvedBy.name ||
                                    "Not approved"}
                            </Col>
                        )}
                        {!isAddingOrg && user!.userLevel! <= 2 && (
                            <Col md={2}>
                                <Form.Label>Approved On</Form.Label>
                                <br />
                                {organization?.approvalDate
                                    ? defaultDateFormat.format(
                                          new Date(organization?.approvalDate),
                                      )
                                    : "Not approved"}
                            </Col>
                        )}
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group controlId="primaryUser">
                                <Form.Label>Organization User</Form.Label>
                                <Controller
                                    name="primaryUser"
                                    control={methods.control}
                                    defaultValue={defaultUser}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            isSearchable={true}
                                            defaultValue={defaultUser}
                                            options={users
                                                .filter(u =>
                                                    user!.userLevel! > 2
                                                        ? u.organization ===
                                                          organization?.id
                                                        : true,
                                                )
                                                .map(u => ({
                                                    value: parseInt(
                                                        u.id.toString(),
                                                    ),
                                                    label: `${u.meta.firstName} ${u.meta.lastName} (${u.email})`,
                                                }))}
                                        />
                                    )}
                                />
                                {methods.formState.errors.primaryUser &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: methods.formState.errors
                                            .primaryUser.label
                                            ?.message as string,
                                    })}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Form.Group controlId="majorTeams" as={Col}>
                            <Form.Label>
                                MajorYBL Teams (
                                {watchMajorTeams
                                    ? watchMajorTeams.length
                                    : organization
                                      ? organization?.teams.majorTeams.length
                                      : 0}
                                )
                            </Form.Label>
                            <Controller
                                name="majorTeams"
                                control={methods.control}
                                defaultValue={majorTeams}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        isMulti
                                        isSearchable={true}
                                        options={teamsList}
                                        defaultValue={majorTeams}
                                    />
                                )}
                            />
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group controlId="WITeams" as={Col}>
                            <Form.Label>
                                WI Tournament Teams (
                                {watchWITeams
                                    ? watchWITeams.length
                                    : organization
                                      ? organization?.teams.WITeams.length
                                      : 0}
                                )
                            </Form.Label>
                            <Controller
                                name="WITeams"
                                control={methods.control}
                                defaultValue={WITeams}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        isMulti
                                        isSearchable={true}
                                        options={teamsList}
                                        defaultValue={WITeams}
                                    />
                                )}
                            />
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group controlId="ILTeams" as={Col}>
                            <Form.Label>
                                IL Tournament Teams (
                                {watchILTeams
                                    ? watchILTeams.length
                                    : organization
                                      ? organization?.teams.ILTeams.length
                                      : 0}
                                )
                            </Form.Label>
                            <Controller
                                name="ILTeams"
                                control={methods.control}
                                defaultValue={ILTeams}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        isMulti
                                        isSearchable={true}
                                        options={teamsList}
                                        defaultValue={ILTeams}
                                    />
                                )}
                            />
                        </Form.Group>
                    </Row>
                    <Row>
                        <Col>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={
                                    user!.userLevel! === 4 &&
                                    organization?.approved === 0
                                }
                            >
                                {isAddingOrg
                                    ? "Add Organization"
                                    : "Edit Organization"}
                            </Button>
                            {!isAddingOrg && (
                                <Button
                                    type="button"
                                    variant="danger"
                                    className={classNames("mx-2")}
                                    onClick={() => setShow(true)}
                                >
                                    Delete Organization
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Form>
            </FormProvider>
            {!isAddingOrg && (
                <DeleteOrganizationModal
                    show={show}
                    setShow={setShow}
                    organization={organization}
                />
            )}
        </section>
    );
};

export default Organization;
